import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/serge'
import { cn } from '@/lib/utils'
import { getSergeProjectContext, identifySergeRisks } from '@/services/sergeApi'

interface Risk {
  id: string
  title: string
  description: string
  severity: string
  probability: string
  impact: string
  mitigation?: string
}

interface RisksContext {
  status?: string
  risks?: Risk[]
  globalAssessment?: string
  recommendations?: string[]
}

const severityStyle: Record<string, string> = {
  Haute: 'text-red-500',
  Moyenne: 'text-primary',
  Faible: 'text-foreground-muted',
}

export default function SergeRisksPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isIdentifying, setIsIdentifying] = useState(false)
  const [risksData, setRisksData] = useState<RisksContext | null>(null)

  useEffect(() => {
    if (projectId) {
      loadRisks()
    }
  }, [projectId])

  const loadRisks = async () => {
    try {
      setIsLoading(true)
      const context = await getSergeProjectContext(Number(projectId))
      setRisksData(context.context.risks)
    } catch (error) {
      toast.error('Erreur lors du chargement des risques')
    } finally {
      setIsLoading(false)
    }
  }

  const handleIdentifyRisks = async () => {
    try {
      setIsIdentifying(true)
      const result = await identifySergeRisks(Number(projectId))
      setRisksData(result.risks)
      toast.success('Risques identifies avec succes')
    } catch (error) {
      toast.error('Erreur lors de l\'identification des risques')
    } finally {
      setIsIdentifying(false)
    }
  }

  const handleGoToExport = () => {
    navigate(`/dashboard/serge-projects/${projectId}/export`)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const risks = risksData?.risks || []
  const isRisksIdentified = risksData?.status === 'completed' && risks.length > 0

  const highRisks = risks.filter((r) => r.severity === 'Haute').length
  const mediumRisks = risks.filter((r) => r.severity === 'Moyenne').length
  const lowRisks = risks.filter((r) => r.severity === 'Faible').length

  return (
    <div className="grid grid-cols-12 gap-4">
      <section className="col-span-12 xl:col-span-8 space-y-4">
        {!isRisksIdentified && (
          <div className="border border-border bg-surface p-5">
            <h2 className="text-lg font-semibold text-foreground">Identification des risques</h2>
            <p className="text-sm text-foreground-secondary mt-2">
              {isIdentifying
                ? 'Analyse des risques en cours...'
                : 'Lancez l\'identification automatique des risques bases sur l\'analyse et les clarifications.'}
            </p>
            <div className="mt-4">
              <Button
                variant="primary"
                size="md"
                onClick={handleIdentifyRisks}
                disabled={isIdentifying}
              >
                {isIdentifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Identification en cours...
                  </>
                ) : (
                  'Identifier les risques'
                )}
              </Button>
            </div>
          </div>
        )}

        {risks.map((risk: Risk) => (
          <div key={risk.id} className="border border-border bg-surface p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">{risk.title}</h2>
              <span className={cn('text-xs font-mono uppercase', severityStyle[risk.severity])}>
                {risk.severity}
              </span>
            </div>
            <p className="text-sm text-foreground-secondary mt-2">{risk.detail}</p>
            {risk.category && (
              <span className="inline-block mt-2 text-xs font-mono text-foreground-muted bg-bg px-2 py-1 border border-border">
                {risk.category}
              </span>
            )}
            <div className="border border-border bg-bg p-4 mt-4">
              <p className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                Recommandation
              </p>
              <p className="text-sm text-foreground-secondary mt-2">{risk.recommendation}</p>
            </div>
          </div>
        ))}

        {isRisksIdentified && risks.length === 0 && (
          <div className="border border-border bg-surface p-8 text-center">
            <h2 className="text-lg font-semibold text-foreground">Aucun risque identifie</h2>
            <p className="text-sm text-foreground-secondary mt-2">
              L'analyse n'a pas identifie de risques majeurs pour ce projet.
            </p>
          </div>
        )}
      </section>

      <section className="col-span-12 xl:col-span-4 space-y-4">
        <div className="border border-border bg-surface p-5">
          <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
            Statut global
          </h3>
          {isRisksIdentified ? (
            <>
              <p className="text-sm text-foreground-secondary mt-3">
                {risks.length} risque(s) identifie(s).
                {highRisks > 0 && ` ${highRisks} critique(s) a traiter.`}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="border border-border bg-bg p-2">
                  <p className="text-xl font-bold text-red-500">{highRisks}</p>
                  <p className="text-xs text-foreground-muted">Haute</p>
                </div>
                <div className="border border-border bg-bg p-2">
                  <p className="text-xl font-bold text-primary">{mediumRisks}</p>
                  <p className="text-xs text-foreground-muted">Moyenne</p>
                </div>
                <div className="border border-border bg-bg p-2">
                  <p className="text-xl font-bold text-foreground-muted">{lowRisks}</p>
                  <p className="text-xs text-foreground-muted">Faible</p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-foreground-secondary mt-3">
              Les risques n'ont pas encore ete identifies.
            </p>
          )}
          <div className="mt-4 space-y-3">
            {isRisksIdentified && (
              <Button variant="primary" size="md" className="w-full" onClick={handleGoToExport}>
                Passer a l'export
              </Button>
            )}
            <Button variant="outline" size="md" className="w-full" onClick={loadRisks}>
              Rafraichir
            </Button>
          </div>
        </div>

        {risksData?.globalAssessment && (
          <div className="border border-border bg-surface p-5">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              Evaluation globale
            </h3>
            <p className="text-sm text-foreground-secondary mt-3">{risksData.globalAssessment}</p>
          </div>
        )}

        {risksData?.recommendations && risksData.recommendations.length > 0 && (
          <div className="border border-border bg-surface p-5">
            <h3 className="text-sm font-mono text-foreground-muted uppercase tracking-widest">
              Recommandations generales
            </h3>
            <ul className="mt-3 space-y-2">
              {risksData.recommendations.map((rec, index) => (
                <li key={index} className="text-sm text-foreground-secondary border border-border bg-bg p-3">
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}
